import { ICustomer } from "./ICustomer";
import { BaseEntity, DefaultResponseWithData } from "./system";


export interface IAllPriceSettingsResponse extends DefaultResponseWithData<AllPriceSettings> { };
export interface IManufacturerPriceSettingResponse extends DefaultResponseWithData<IManufacturerPriceSetting> { };
export interface IManufacturerPriceSettingsResponse extends DefaultResponseWithData<IManufacturerPriceSetting[]> { };

export interface AllPriceSettings {
  customerPriceConfiguration: ICustomerPriceConfiguration | null;
  genericPriceSettings: ICustomerGenericPriceSetting[];
  salvagePriceSettings: ICustomerSalvagePriceSetting[];
  manufacturerPriceSettings: IManufacturerPriceSetting[];
}


export interface ICustomerPriceConfiguration extends BaseEntity {
  doesCollection: boolean;
  doesDropOff: boolean;
  dropOffPricePerTonne: number | null;
  dropOffPricingType: DropOffPricingType;
  dropOffFlatPrice: number | null;
  /** Off, the flat price replaces only the weight based base. On, it is the whole drop off quote. */
  dropOffFlatIgnoresRules: boolean;

  customerId: string;
  customer: ICustomer | null;

  collectionPriceSettingId: string | null;
  collectionPriceSetting: IPriceSetting | null;

  dropOffPriceSettingId: string | null;
  dropOffPriceSetting: IPriceSetting | null;

  alloyWheelsPriceSettingId: string | null;
  alloyWheelsPriceSetting: IPriceSetting | null;

  noFaultPriceSettingId: string | null;
  noFaultPriceSetting: IPriceSetting | null;

  startsAndDrivesPriceSettingId: string | null;
  startsAndDrivesPriceSetting: IPriceSetting | null;

  maxSalvageMileage: number | null;

  engineExportThreshold: number | null;
  vfeExportThreshold: number | null;

  allowBikes: boolean;
  // Null prices bikes like any other vehicle
  bikeFixedPrice: number | null;

  // catalyticConverterPriceSettingId: string | null;
  // catalyticConverterPriceSetting: IPriceSetting | null;

  // exportPriceSettingId: string | null;
  // exportPriceSetting: IPriceSetting | null;
  customerPriceRules: ICustomerPriceRule[];
}

export interface ICustomerPriceRule extends BaseEntity {
  value: number;
  type: CustomerPriceRuleType;
  condition: PriceCondition;

  customerPriceConfigurationId: string;
  customerPriceConfiguration: ICustomerPriceConfiguration | null;

  priceSettingId: string;
  priceSetting: IPriceSetting | null;

  engineCondition: RuleCondition | null;
  bodyCondition: RuleCondition | null;

  yearCondition: PriceCondition;
  yearValue: number;
}


export interface ICustomerGenericPriceSetting extends BaseEntity {
  make: string | null;
  model: string | null;
  fromYear: number | null;
  toYear: number | null;
  fuelType: string | null;
  engineCode: string | null;
  engineCodeMatchType: EngineCodeMatchType;
  fromEngineCc: number | null;
  toEngineCc: number | null;
  transmissionType: string | null;
  maximumPrice: number | null;

  fixedPrice: number | null;
  goodEngineFixedPrice: number | null;
  minorFaultFixedPrice: number | null;
  majorFaultFixedPrice: number | null;

  partsLookup: boolean;

  isActive: boolean;
  /** When it adds its top up, no other rule's top up is added. */
  isExclusive: boolean;
  appliesToScrap: boolean;
  appliesToSalvage: boolean;
  appliesToSmart: boolean;
  ruleId: string | null;

  minBodyCondition: BodyCondition | null;

  customerPriceConfigurationId: string;
  customerPriceConfiguration: ICustomerPriceConfiguration | null;
  
  priceSettingId: string | null;
  priceSetting: IPriceSetting | null;

  goodEnginePriceSettingId: string | null;
  goodEnginePriceSetting: IPriceSetting | null;

  minorFaultPriceSettingId: string | null;
  minorFaultPriceSetting: IPriceSetting | null;

  majorFaultPriceSettingId: string | null;
  majorFaultPriceSetting: IPriceSetting | null;
}


export interface ICustomerSalvagePriceSetting extends BaseEntity {
  engineCondition: EngineCondition;
  bodyCondition: BodyCondition;
  customerPriceConfigurationId: string;
  customerPriceConfiguration: ICustomerPriceConfiguration | null;
  priceSettingId: string;
  priceSetting: IPriceSetting | null;
}

export interface IManufacturerPriceSetting extends BaseEntity {
  // value/toValue bound the trade poor band this setting applies to.
  // value is the lower bound (exclusive), toValue the upper bound (inclusive),
  // and null on either side means unbounded.
  value: number | null;
  toValue: number | null;
  year: number | null;
  make: string | null;

  customerPriceConfigurationId: string;
  customerPriceConfiguration: ICustomerPriceConfiguration | null;

  priceSettingId: string;
  priceSetting: IPriceSetting | null;
}

export interface IPriceSetting extends BaseEntity {
  isFixed: boolean;
  value: number;
}

export enum DropOffPricingType {
  PerTonne,
  Flat
}

export enum EngineCondition {
  NoFault,
  MinorFault,
  MajorFault
}

export enum BodyCondition {
  Good,
  Average,
  HeavyDamage
}

export enum EngineCodeMatchType {
  Contains,
  StartsWith,
  Exact
}

export enum CustomerPriceRuleType {
  CatalyticConverter,
  Export,
  VfeExport,
  PartsLookup
}

export enum PriceCondition {
  LessThan,
  LessThanOrEqual,
  Equal,
  GreaterThanOrEqual,
  GreaterThan
}

export enum RuleCondition
{
  All,
  Good,
  GoodOrAverage,
  Average,
  AverageOrPoor,
  Poor
}

export interface ISalvageCalculation {
  name: string;
  isFixed: boolean;
  value: number;
  baseValue: number;
  isAddition: boolean;
  customCalculation: string;
  dropOff: boolean;
  priceSettingId: string | null;
}

export interface ISalvageRuleTestRequest {
  vrm: string;
  engineCondition: EngineCondition;
  bodyCondition: BodyCondition;
}

export interface ISalvageRuleTestResult {
  vrm: string;
  make: string | null;
  model: string | null;
  year: number;
  odometerInMiles: number;
  tradePoor: number;
  hasSalvageValue: boolean;
  finalValue: number;
  gatedByDoesSalvage: boolean;
  gatedByMileage: boolean;
  maxSalvageMileage: number;
  gatedByZeroManufacturerValue: boolean;
  gatedByZeroFinalValue: boolean;
  gatingReason: string | null;
  manufacturerMatch: string | null;
  manufacturerFrom: number | null;
  manufacturerTo: number | null;
  manufacturerYearGated: boolean;
  manufacturerValueGated: boolean;
  salvageCalculations: ISalvageCalculation[];
  tradePoorCalculations: ISalvageCalculation[];
}

export enum PricingContext {
  Scrap,
  Salvage,
  Smart
}

export interface ISpecificRuleTestRequest {
  vrm: string;
  engineCondition: EngineCondition;
  bodyCondition: BodyCondition;
  context: PricingContext;
}

export interface ISpecificRuleTestVehicle {
  make: string | null;
  model: string | null;
  year: number;
  fuelType: string | null;
  engineCode: string | null;
  engineCc: number | null;
  transmission: string | null;
}

export interface ISpecificRuleTestResult {
  id: string;
  ruleId: string | null;
  isActive: boolean;
  isExclusive: boolean;
  conditions: string[];
  reasons: string[];
  /** Remarks on a rule that did match, where its price alone doesn't say what it does. */
  notes: string[];
  satisfiedConditions: number;
  isFixedPrice: boolean;
  /** The price is a fixed price, which replaces the quote outright rather than adding to it. */
  isStaticOverride: boolean;
  /** The rule matched but only caps the quote — it has no top up to add. */
  capsOnly: boolean;
  price: number | null;
  maximumPrice: number | null;
}

export interface ISpecificRuleTestResponse {
  vrm: string;
  vehicleFound: boolean;
  message: string | null;
  vehicle: ISpecificRuleTestVehicle;
  matched: ISpecificRuleTestResult[];
  unmatched: ISpecificRuleTestResult[];
  /** Id of the matched fixed price rule that would decide the quote on its own, if there is one. */
  staticOverrideId: string | null;
  /** Id of the matched exclusive rule whose top up is the only one added, if there is one. */
  exclusiveRuleId: string | null;
  /** The tightest cap among the matched rules — the one the quote is actually held to. */
  effectiveMaximumPrice: number | null;
}